import { isReleaseInfoUser, useAuth } from "@assetwatch/auth";
import {
  AppShell,
  AppShellContent,
  AppShellMobileNavigationTrigger,
  AssetWatchLogo,
  Banner,
  Button,
  Chip,
  FullPageLoading,
  IconButton,
  Label,
  Select,
  Sidebar,
  SidebarGroup,
  SidebarItem,
  TopBar,
  TopBarActions,
  TopBarLeading,
  useSidebar,
  useTheme,
} from "@assetwatch/ui";
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AskReliChatDialog } from "../components/AskReliCustomerChat";
import { EnterpriseCustomerAccessCard } from "../components/EnterpriseCustomerState";
import {
  EnterprisePageChromeProvider,
  useEnterprisePageBanner,
} from "../components/EnterprisePageChrome";
import { EnterpriseProfileMenu } from "../components/EnterpriseProfileMenu";
import { EnterpriseReleaseInfo } from "../components/EnterpriseReleaseInfo";
import { EnterpriseRouteFrame } from "../components/EnterpriseRouteFrame";
import { FacilityDetailDrawer } from "../components/FacilityDetailDrawer";
import { useFeatureFlags } from "../components/LaunchDarkly/useFeatureFlags";
import { MaturityScoreHelpOverlay } from "../components/MaturityScoreHelpOverlay";
import { useResolvedEnterpriseCustomer } from "../hooks/useResolvedEnterpriseCustomer";
import { isInternalEnterpriseUser } from "../lib/enterpriseAccess";
import { ensureEnterpriseCustomerContext } from "../lib/enterpriseCustomerContext";
import { prefetchFacilityDetailQueries } from "../lib/prefetchFacilityDetail";
import {
  setEnterpriseCustomerSentryContext,
  setEnterpriseRouteSentryContext,
} from "../lib/sentry";
import { useEnterpriseStore } from "../state/enterpriseStore";

const customerSearchSchema = z.object({
  externalCustomerId: z.string().trim().min(1).optional(),
  askReli: z.preprocess((val) => {
    if (val === true || val === "true" || val === "1") return true;
    return undefined;
  }, z.literal(true).optional()),
  /** Facility side sheet; set with tab for the active facility tab. */
  facilityId: z.preprocess((val) => {
    if (val === undefined || val === null || val === "") return undefined;
    const n = Number(val);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, z.number().int().positive().optional()),
  /** Used with facilityId for facility profile tabs; ignored otherwise. */
  tab: z.enum(["overview", "maturity", "value", "achievements"]).optional(),
});

const primaryNav = [
  {
    to: "/" as const,
    labelKey: "enterprise.routes.appShell.nav.overview",
    icon: "home",
  },
  {
    to: "/assets" as const,
    labelKey: "enterprise.routes.appShell.nav.assets",
    icon: "asset",
  },
  {
    to: "/team" as const,
    labelKey: "enterprise.routes.appShell.nav.team",
    icon: "customers",
  },
  {
    to: "/maturity" as const,
    labelKey: "enterprise.routes.appShell.nav.maturityModel",
    icon: "trend-up",
  },
  {
    to: "/settings" as const,
    labelKey: "enterprise.routes.appShell.nav.settings",
    icon: "settings-cog",
  },
] as const;

const comingSoonNav = [
  {
    to: "/map" as const,
    labelKey: "enterprise.routes.appShell.nav.map",
    icon: "location-pin-solid",
  },
  {
    to: "/cmms" as const,
    labelKey: "enterprise.routes.appShell.nav.cmmsIntelligence",
    icon: "integrations",
  },
  {
    to: "/achievements" as const,
    labelKey: "enterprise.routes.appShell.nav.achievements",
    icon: "star-recommended",
  },
  {
    to: "/sandbox" as const,
    labelKey: "enterprise.routes.appShell.nav.sandboxSummary",
    icon: "binoculars",
  },
  {
    to: "/assets-v2" as const,
    labelKey: "enterprise.assetsV2.pageTitle",
    icon: "asset",
  },
] as const;

const NO_CUSTOMER_VALUE = "__select_customer__";
function EnterpriseAppLayout() {
  return (
    <EnterprisePageChromeProvider>
      <EnterpriseAppShell />
    </EnterprisePageChromeProvider>
  );
}

function EnterpriseAppShell() {
  const { t } = useTranslation();
  const { signOut, user } = useAuth();
  const pageBanner = useEnterprisePageBanner();
  useTheme();
  const [isBetaBannerVisible, setIsBetaBannerVisible] = useState(true);
  const [isReleaseInfoOpen, setIsReleaseInfoOpen] = useState(false);
  const isAskReliOpen = useEnterpriseStore((state) => state.isAskReliOpen);
  const openAskReli = useEnterpriseStore((state) => state.openAskReli);
  const closeAskReli = useEnterpriseStore((state) => state.closeAskReli);
  const isMaturityHelpOpen = useEnterpriseStore(
    (state) => state.isMaturityHelpOpen,
  );
  const setMaturityHelpOpen = useEnterpriseStore(
    (state) => state.setMaturityHelpOpen,
  );
  const setExternalCustomerId = useEnterpriseStore(
    (state) => state.setExternalCustomerId,
  );
  const { askReli, externalCustomerId: routeExternalCustomerId } =
    Route.useSearch();
  const { "enterprise-assets-v2": assetsV2Enabled } = useFeatureFlags();
  const navigate = useNavigate();
  const {
    pathname,
    resolvedPathname,
    isLoading,
    pendingExternalCustomerId,
    activeExternalCustomerId,
    PageHeader,
  } = useRouterState({
    select: (state) => {
      const locationSearch = state.location.search as
        | { externalCustomerId?: string }
        | undefined;
      const resolvedSearch = state.resolvedLocation?.search as
        | { externalCustomerId?: string }
        | undefined;

      return {
        pathname: state.location.pathname,
        resolvedPathname: state.resolvedLocation?.pathname,
        isLoading: state.isLoading,
        pendingExternalCustomerId: locationSearch?.externalCustomerId,
        activeExternalCustomerId: resolvedSearch?.externalCustomerId,
        PageHeader: state.matches.at(-1)?.staticData.pageHeader,
      };
    },
  });
  const isInternalUser = isInternalEnterpriseUser(user);
  const canViewReleaseInfo = isInternalUser && isReleaseInfoUser(user);
  const {
    selectableCustomers,
    isResolvingExternalCustomer,
    hasResolvedExternalCustomer,
    hasCustomerResolutionError,
    resolvedExternalCustomerId,
  } = useResolvedEnterpriseCustomer(routeExternalCustomerId);
  const externalCustomerId =
    routeExternalCustomerId ?? resolvedExternalCustomerId;

  const isSwitchingCustomer =
    isLoading && pendingExternalCustomerId !== activeExternalCustomerId;
  const isSwitchingPage =
    isLoading && pathname !== resolvedPathname && !isSwitchingCustomer;
  const askReliExternalCustomerId = isSwitchingCustomer
    ? activeExternalCustomerId
    : externalCustomerId;

  useEffect(() => {
    if (isAskReliOpen && isSwitchingCustomer) {
      closeAskReli();
    }
  }, [closeAskReli, isAskReliOpen, isSwitchingCustomer]);

  useEffect(() => {
    setExternalCustomerId(externalCustomerId ?? null);
    setEnterpriseCustomerSentryContext(externalCustomerId ?? null);
  }, [externalCustomerId, setExternalCustomerId]);

  useEffect(() => {
    setEnterpriseRouteSentryContext(resolvedPathname ?? pathname);
  }, [pathname, resolvedPathname]);

  useEffect(() => {
    if (!askReli) {
      return;
    }

    openAskReli();
    void navigate({
      to: pathname,
      replace: true,
      search: (previous) => {
        const nextSearch = { ...(previous ?? {}) };
        delete nextSearch.askReli;
        return nextSearch;
      },
    });
  }, [askReli, navigate, openAskReli, pathname]);

  useEffect(() => {
    if (
      isInternalUser ||
      routeExternalCustomerId ||
      !resolvedExternalCustomerId
    ) {
      return;
    }

    void navigate({
      to: pathname,
      replace: true,
      search: (previous) => ({
        ...(previous ?? {}),
        externalCustomerId: resolvedExternalCustomerId,
      }),
    });
  }, [
    isInternalUser,
    navigate,
    pathname,
    resolvedExternalCustomerId,
    routeExternalCustomerId,
  ]);

  if (!isInternalUser && isResolvingExternalCustomer) {
    return <FullPageLoading />;
  }

  if (
    !isInternalUser &&
    !routeExternalCustomerId &&
    hasResolvedExternalCustomer
  ) {
    return <FullPageLoading />;
  }

  if (!isInternalUser && hasCustomerResolutionError) {
    return <EnterpriseCustomerAccessCard />;
  }

  const customerSelector = isInternalUser ? (
    <EnterpriseCustomerSelector
      disabled={isSwitchingCustomer}
      label={t("enterprise.routes.appShell.customerSelectorLabel")}
      options={[
        {
          value: NO_CUSTOMER_VALUE,
          label: t("enterprise.routes.appShell.selectCustomerOption"),
        },
        ...(selectableCustomers ?? []).map((item) => ({
          value: item.externalCustomerId,
          label: item.customerName,
        })),
      ]}
      value={externalCustomerId ?? NO_CUSTOMER_VALUE}
      onChange={(value) => {
        const nextExternalCustomerId =
          value === NO_CUSTOMER_VALUE ? undefined : value || undefined;
        void navigate({
          to: "/",
          search: (previous) => {
            const safePrevious = previous ?? {};

            if (nextExternalCustomerId) {
              const next = {
                ...safePrevious,
                externalCustomerId: nextExternalCustomerId,
              };
              delete (next as { facilityId?: number; tab?: string }).facilityId;
              delete (next as { facilityId?: number; tab?: string }).tab;
              return next;
            }

            const { externalCustomerId: _externalCustomerId, ...rest } =
              safePrevious;
            return rest;
          },
        });
      }}
    />
  ) : null;

  const maturityHelpAction =
    pathname === "/maturity" ? (
      <IconButton
        variant="outline"
        size="md"
        icon="circle-info"
        aria-label={t("enterprise.routes.appShell.maturityHelpAriaLabel")}
        onClick={() => setMaturityHelpOpen(true)}
      />
    ) : null;

  const applicationActions = (
    <>
      <Button
        variant="ai-outline"
        size="md"
        onClick={isAskReliOpen ? closeAskReli : openAskReli}
        data-testid="nav-ask-reli"
        type="button"
        aria-expanded={isAskReliOpen}
        aria-haspopup="dialog"
      >
        {t("enterprise.reliability.assistant.title")}
      </Button>
      {maturityHelpAction}
      <EnterpriseProfileMenu
        canViewReleaseInfo={canViewReleaseInfo}
        onOpenReleaseInfo={() => setIsReleaseInfoOpen(true)}
        onSignOut={() => void signOut()}
      />
    </>
  );

  const mobileApplicationActions = maturityHelpAction;

  return (
    <AppShell>
      <TopBar>
        <TopBarLeading>
          <AppShellMobileNavigationTrigger />
          <div className="hidden min-w-0 items-end gap-(--spacing-8) min-[600px]:flex uppercase">
            <AssetWatchLogo
              aria-label={t("enterprise.common.assetWatchLabel")}
            />
            <Label size="xs" tone="subtle">
              {t("enterprise.common.enterpriseLabel")}
            </Label>
            <Chip
              tone="neutral"
              label={t("enterprise.routes.appShell.betaBadge")}
            />
          </div>
        </TopBarLeading>
        <TopBarActions>{applicationActions}</TopBarActions>
      </TopBar>
      <Sidebar
        aria-label={t("enterprise.routes.appShell.enterpriseSectionsAriaLabel")}
        data-testid="enterprise-sidebar"
        mobileHeaderActions={mobileApplicationActions}
        mobileHeaderLeading={
          <div className="flex min-w-0 items-end gap-(--spacing-8) uppercase">
            <AssetWatchLogo
              aria-label={t("enterprise.common.assetWatchLabel")}
            />
            <Label size="xs" tone="subtle">
              {t("enterprise.common.enterpriseLabel")}
            </Label>
            <Chip
              tone="neutral"
              label={t("enterprise.routes.appShell.betaBadge")}
            />
          </div>
        }
      >
        {customerSelector}
        {primaryNav.map(({ to, labelKey, icon }) => (
          <SidebarItem
            key={to}
            itemId={to === "/" ? "summary" : to.slice(1)}
            label={t(labelKey)}
            leading={{ type: "icon", name: icon }}
            selected={pathname === to}
            data-testid={`nav-${to === "/" ? "summary" : to.slice(1)}`}
            render={
              <Link
                to={to}
                search={externalCustomerId ? { externalCustomerId } : undefined}
              />
            }
          />
        ))}
        <SidebarGroup
          itemId="coming-soon"
          label={t("enterprise.routes.appShell.comingSoonLabel")}
          collapsible={false}
        >
          {comingSoonNav
            .filter(({ to }) => to !== "/assets-v2" || assetsV2Enabled)
            .map(({ to, labelKey, icon }) => (
              <SidebarItem
                key={to}
                itemId={to.slice(1)}
                label={t(labelKey)}
                leading={{ type: "icon", name: icon }}
                selected={pathname === to}
                data-testid={`nav-${to.slice(1)}`}
                render={
                  <Link
                    to={to}
                    search={
                      externalCustomerId ? { externalCustomerId } : undefined
                    }
                  />
                }
              />
            ))}
        </SidebarGroup>
      </Sidebar>
      <AskReliChatDialog
        key={askReliExternalCustomerId}
        open={isAskReliOpen && !isSwitchingCustomer}
        externalCustomerId={askReliExternalCustomerId}
        onClose={closeAskReli}
      />
      <AppShellContent
        pending={isSwitchingPage || isSwitchingCustomer}
        privacyPolicyLabel={t("enterprise.routes.appShell.privacyPolicy")}
        pageHeader={
          externalCustomerId && PageHeader ? <PageHeader /> : undefined
        }
        banner={
          isBetaBannerVisible ? (
            <Banner
              data-testid="enterprise-beta-banner"
              icon="circle-info"
              title={t("enterprise.routes.appShell.betaBannerTitle")}
              description={t("enterprise.routes.appShell.betaBannerBody")}
              onDismiss={() => setIsBetaBannerVisible(false)}
            />
          ) : undefined
        }
      >
        <EnterpriseRouteFrame banner={pageBanner}>
          <Outlet />
        </EnterpriseRouteFrame>
      </AppShellContent>
      <MaturityScoreHelpOverlay
        open={isMaturityHelpOpen}
        onClose={() => setMaturityHelpOpen(false)}
      />
      <Suspense fallback={null}>
        <FacilityDetailDrawer />
      </Suspense>
      {canViewReleaseInfo ? (
        <EnterpriseReleaseInfo
          open={isReleaseInfoOpen}
          onOpenChange={setIsReleaseInfoOpen}
        />
      ) : null}
    </AppShell>
  );
}

type EnterpriseCustomerSelectorProps = {
  disabled: boolean;
  label: string;
  onChange: (value: string | null) => void;
  options: Array<{ label: string; value: string }>;
  value: string;
};

function EnterpriseCustomerSelector({
  disabled,
  label,
  onChange,
  options,
  value,
}: EnterpriseCustomerSelectorProps) {
  const { collapsed } = useSidebar();

  return (
    <section aria-label={label} className="flex flex-col gap-(--spacing-4)">
      {collapsed ? (
        <div aria-hidden className="flex h-(--spacing-40) items-center">
          <span className="w-full border-t border-outline-subtle" />
        </div>
      ) : (
        <span className="flex h-(--spacing-40) items-center whitespace-nowrap px-(--spacing-10) text-(length:--font-size-label-medium) font-(number:--font-weight-medium) text-text-subtle uppercase">
          {label}
        </span>
      )}
      <div className={collapsed ? "flex w-full justify-center" : undefined}>
        <div className={collapsed ? "w-[60px] shrink-0" : undefined}>
          <Select
            data-testid="nav-customer-selector"
            disabled={disabled}
            options={options}
            popupWidth="content"
            reserveSubtextSpace={false}
            size="md"
            value={value}
            onChange={onChange}
          />
        </div>
      </div>
    </section>
  );
}

export const Route = createFileRoute("/_app")({
  validateSearch: (search) => customerSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({
    externalCustomerId: search.externalCustomerId,
    facilityId: search.facilityId,
  }),
  loader: async ({ context, deps }) => {
    if (!deps.externalCustomerId) {
      return null;
    }

    const customer = await ensureEnterpriseCustomerContext(
      context.queryClient,
      deps.externalCustomerId,
    );

    if (deps.facilityId !== null && deps.facilityId !== undefined) {
      await prefetchFacilityDetailQueries(
        context.queryClient,
        customer.customerId,
        deps.facilityId,
      );
    }

    return customer;
  },
  component: EnterpriseAppLayout,
});
